import { Suspense } from 'react'
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom'
import MainLayout from './layouts/MainLayout'
import ScrollToTop from './components/ScrollToTop'
import MetaPixelPageView from './components/MetaPixelPageView'
import LoadingSpinner from './components/LoadingSpinner'
import { lazyWithReload as lazy } from './utils/lazyWithReload'

// Eager: the public ad/marketing funnel. These ship in the entry chunk so an
// ad click lands with no extra round-trip.
import HomeV2 from './pages/HomeV2'
import Events from './pages/Events'
import EventDetail from './pages/EventDetail'
import RegistrationSuccess from './pages/RegistrationSuccess'
import PaymentRefund from './pages/PaymentRefund'
import Login from './pages/Login'
import NotFound from './pages/NotFound'

// Lazy: everything else becomes its own chunk, fetched on first visit.
const DashboardConcept = lazy(() => import('./pages/DashboardConcept'))
const Onboarding = lazy(() => import('./pages/Onboarding'))
const WellnessOnboarding = lazy(() => import('./pages/WellnessOnboarding'))
const EventFeedbackForm = lazy(() => import('./pages/EventFeedbackForm'))
const FacilitatorBookings = lazy(() => import('./pages/FacilitatorBookings'))
const Facilitators = lazy(() => import('./pages/Facilitators'))
const FacilitatorOnboarding = lazy(() => import('./pages/FacilitatorOnboarding'))
const CreateFacilitator = lazy(() => import('./pages/CreateFacilitator'))
const FacilitatorDetail = lazy(() => import('./pages/FacilitatorDetail'))
const FacilitatorConsultationManage = lazy(() => import('./pages/FacilitatorConsultationManage'))
const FacilitatorClassManage = lazy(() => import('./pages/FacilitatorClassManage'))
const FacilitatorGalleryManage = lazy(() => import('./pages/FacilitatorGalleryManage'))
const FacilitatorTestimonialManage = lazy(() => import('./pages/FacilitatorTestimonialManage'))
const FacilitatorResourceManage = lazy(() => import('./pages/FacilitatorResourceManage'))
const FacilitatorConsultations = lazy(() => import('./pages/FacilitatorConsultations'))
const FacilitatorSlugEvents = lazy(() => import('./pages/FacilitatorSlugEvents'))
const SpaceDetail = lazy(() => import('./pages/SpaceDetail'))
const Meetup = lazy(() => import('./pages/Meetup'))
const PersonalizedJourneys = lazy(() => import('./pages/PersonalizedJourneys'))
const Build3Interest = lazy(() => import('./pages/Build3Interest'))
const WellnessCollaborative = lazy(() => import('./pages/WellnessCollaborative'))
const CohortSignup = lazy(() => import('./pages/CohortSignup'))
const Build3CohortDashboard = lazy(() => import('./pages/Build3CohortDashboard'))
const CohortAuthVerify = lazy(() => import('./pages/CohortAuthVerify'))
const MidDayReset = lazy(() => import('./pages/MidDayReset'))
const MidDayResetCheckIn = lazy(() => import('./pages/MidDayResetCheckIn'))
const MidDayResetDashboard = lazy(() => import('./pages/MidDayResetDashboard'))
const SessionCheckIn = lazy(() => import('./pages/SessionCheckIn'))
const SessionFormResponses = lazy(() => import('./pages/SessionFormResponses'))
const ParticipantDashboard = lazy(() => import('./pages/ParticipantDashboard'))
const FacilitatorDashboard = lazy(() => import('./pages/FacilitatorDashboard'))
const FacilitatorEvents = lazy(() => import('./pages/FacilitatorEvents'))
const TestimonialSubmit = lazy(() => import('./pages/TestimonialSubmit'))
const AdminLogin = lazy(() => import('./pages/AdminLogin'))
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'))
const TestSentryError = lazy(() => import('./pages/TestSentryError'))
const CorporateLanding = lazy(() => import('./pages/CorporateLanding'))
const FacilitatorLanding = lazy(() => import('./pages/FacilitatorLanding'))
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'))
const TermsOfService = lazy(() => import('./pages/TermsOfService'))
const YogaDay = lazy(() => import('./pages/YogaDay'))
const AuraSpa = lazy(() => import('./pages/AuraSpa'))

const COHORT_7_SLUG = 'build3-cohort-7'
const MARCH_COHORT_SLUG = 'energizers-march-cohort'
const JUNE_COHORT_SLUG = 'energizers-june-cohort'
const SEPT_2026_COHORT_SLUG = 'energizers-sept-2026-cohort'

const RouteFallback = () => (
  <div className="min-h-screen flex items-center justify-center">
    <LoadingSpinner size="lg" />
  </div>
)

function App() {
  return (
    <Router>
      <ScrollToTop />
      <MetaPixelPageView />
      <Suspense fallback={<RouteFallback />}>
      <Routes>
        {/* Dashboard route outside MainLayout to use its own navbar */}
        {/* TODO: See if you can combined the build3 routes into a single route */}
        <Route
          path={`${COHORT_7_SLUG}/dashboard`}
          element={<Build3CohortDashboard programSlug={COHORT_7_SLUG} />}
        />
        <Route
          path={`${MARCH_COHORT_SLUG}/dashboard`}
          element={<Build3CohortDashboard programSlug={MARCH_COHORT_SLUG} />}
        />
        <Route
          path={`${JUNE_COHORT_SLUG}/dashboard`}
          element={<Build3CohortDashboard programSlug={JUNE_COHORT_SLUG} />}
        />
        <Route
          path={`${SEPT_2026_COHORT_SLUG}/dashboard`}
          element={<Build3CohortDashboard programSlug={SEPT_2026_COHORT_SLUG} />}
        />

        {/* Mid Day Reset standalone pages */}
        <Route path="mid-day-reset" element={<MidDayReset />} />
        <Route path="mid-day-reset/check-in" element={<MidDayResetCheckIn />} />
        <Route path="mid-day-reset/dashboard" element={<MidDayResetDashboard />} />

        {/* Session check-in standalone pages */}
        <Route
          path="build3-energizer-session/:sessionNumber"
          element={<SessionCheckIn programSlug={COHORT_7_SLUG} />}
        />
        <Route
          path={`${MARCH_COHORT_SLUG}/session/:sessionNumber`}
          element={<SessionCheckIn programSlug={MARCH_COHORT_SLUG} />}
        />
        <Route
          path={`${JUNE_COHORT_SLUG}/session/:sessionNumber`}
          element={<SessionCheckIn programSlug={JUNE_COHORT_SLUG} />}
        />
        <Route
          path={`${SEPT_2026_COHORT_SLUG}/session/:sessionNumber`}
          element={<SessionCheckIn programSlug={SEPT_2026_COHORT_SLUG} />}
        />

        {/* Session form responses (facilitator/admin view) */}
        <Route
          path="programs/:slug/session/:sessionNumber/form-responses"
          element={<SessionFormResponses />}
        />

        {/* Standalone login page (outside MainLayout) */}
        <Route path="login" element={<Login />} />

        {/* Testimonial submission via invite token (standalone) */}
        <Route path="testimonial/:token" element={<TestimonialSubmit />} />

        {/* Admin pages - standalone (outside MainLayout) */}
        <Route path="admin/login" element={<AdminLogin />} />
        <Route path="admin" element={<AdminDashboard />} />
        <Route path="corporates" element={<CorporateLanding />} />
        <Route path="for-facilitators" element={<FacilitatorLanding />} />
        <Route path="home-v2" element={<HomeV2 />} />

        <Route path="/" element={<MainLayout />}>
          <Route index element={<HomeV2 />} />
          <Route path="onboarding" element={<Onboarding />} />
          <Route path="onboarding-wellness" element={<WellnessOnboarding />} />
          <Route path="dashboard-concept" element={<DashboardConcept />} />
          <Route path="dashboard" element={<ParticipantDashboard />} />
          <Route path="events" element={<Events />} />
          <Route path="events/registration-success" element={<RegistrationSuccess />} />
          <Route path="events/payment-refund" element={<PaymentRefund />} />
          <Route path="events/yoga-day" element={<YogaDay />} />
          <Route path="events/:slug" element={<EventDetail />} />
          <Route path="events/:slug/feedback" element={<EventFeedbackForm />} />
          <Route path="facilitators" element={<Facilitators />} />
          <Route path="facilitators/create" element={<CreateFacilitator />} />
          <Route path="facilitators/register" element={<FacilitatorOnboarding />} />
          <Route path="facilitators/edit-profile" element={<FacilitatorOnboarding />} />
          <Route path="facilitator/dashboard" element={<FacilitatorDashboard />} />
          <Route path="facilitator/events" element={<FacilitatorEvents />} />
          <Route path="facilitator/bookings" element={<FacilitatorBookings />} />
          <Route path="facilitator/consultations" element={<FacilitatorConsultationManage />} />
          <Route path="facilitator/classes" element={<FacilitatorClassManage />} />
          <Route path="facilitator/gallery" element={<FacilitatorGalleryManage />} />
          <Route path="facilitator/testimonials" element={<FacilitatorTestimonialManage />} />
          <Route path="facilitator/resources" element={<FacilitatorResourceManage />} />
          <Route path="facilitator/:slug/events" element={<FacilitatorSlugEvents />} />
          <Route path="facilitator/:slug/consultations" element={<FacilitatorConsultations />} />
          <Route path="facilitator/:slug" element={<FacilitatorDetail />} />
          <Route path="space/:id" element={<SpaceDetail />} />
          <Route path="aura-spa" element={<AuraSpa />} />
          <Route path="meetup/:id" element={<Meetup />} />
          <Route path="meetup/:id/personalized-journeys" element={<PersonalizedJourneys />} />
          <Route path="build3-interest" element={<Build3Interest />} />
          <Route path="wellness-collaborative" element={<WellnessCollaborative />} />
          <Route
            path={`${COHORT_7_SLUG}-signup`}
            element={<CohortSignup programSlug={COHORT_7_SLUG} />}
          />
          <Route
            path={`${COHORT_7_SLUG}-auth/verify`}
            element={<CohortAuthVerify programSlug={COHORT_7_SLUG} />}
          />
          <Route
            path={`${MARCH_COHORT_SLUG}-signup`}
            element={<CohortSignup programSlug={MARCH_COHORT_SLUG} />}
          />
          <Route
            path={`${MARCH_COHORT_SLUG}-auth/verify`}
            element={<CohortAuthVerify programSlug={MARCH_COHORT_SLUG} />}
          />
          <Route
            path={`${JUNE_COHORT_SLUG}-signup`}
            element={<CohortSignup programSlug={JUNE_COHORT_SLUG} />}
          />
          <Route
            path={`${SEPT_2026_COHORT_SLUG}-signup`}
            element={<CohortSignup programSlug={SEPT_2026_COHORT_SLUG} />}
          />
          <Route path="privacy-policy" element={<PrivacyPolicy />} />
          <Route path="terms-of-service" element={<TermsOfService />} />
          <Route path="test-sentry-error-459219" element={<TestSentryError />} />
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
      </Suspense>
    </Router>
  )
}

export default App
